import { Component, computed, effect, ElementRef, inject, untracked } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import { getAbsenteeOptionEntities } from '@reducers/orm/absentee-option/absentee-option.service';
import { getDepartmentEntities } from '@reducers/orm/department/department.service';
import { getEmployeeEntities, getEmployeeTeamDepartments } from '@reducers/orm/employee/employee.selectors';
import { getLocationEntities } from '@reducers/orm/location/location.service';
import { getShiftEntities } from '@reducers/orm/shift/shift.service';
import { getTeamEntities } from '@reducers/orm/team/team.service';
import { mgcIconClipboardRegular } from '@sb/mgc-icons/mgcIcon-clipboard_regular.icon';
import { mgcIconWarningRegular } from '@sb/mgc-icons/mgcIcon-warning_regular.icon';
import { ButtonComponent } from '@sb/ui/button';
import { EmptyStateComponent, LoadingStateComponent } from '@sb/ui/feedback';
import { IconRegistry } from '@sb/ui/icon';
import { SubscriptionPlanDirective } from '@shared/subscription-plan/subscription-plan.directive';
import { PlanType } from '@shiftbase-com/ts-shared/models';

import { AbsenceRequestNotificationsComponent } from '../absence-request-notifications.component';
import { ExchangeRequestContext, ExchangeRequestsComponent } from '../exchange-requests.component';
import { PagingStatusComponent } from '../requests/paging-status.component';
import {
  filterIncomingExchanges,
  filterOutgoingExchanges,
  toOwnPendingAbsences,
  toVisibleExchanges,
  toVisibleOpenShifts,
  useExchangeViewContext,
} from '../requests/request-rows.helper';
import { MyRequestsPanelStore } from './my-requests-panel.store';
import { OpenShiftsComponent } from './open-shifts.component';

@Component({
  selector: 'my-requests-content',
  templateUrl: './my-requests-content.component.html',
  imports: [
    ExchangeRequestsComponent,
    OpenShiftsComponent,
    AbsenceRequestNotificationsComponent,
    EmptyStateComponent,
    LoadingStateComponent,
    ButtonComponent,
    TranslatePipe,
    SubscriptionPlanDirective,
    PagingStatusComponent,
  ],
  providers: [MyRequestsPanelStore],
  host: { tabindex: '-1' },
})
export class MyRequestsContentComponent {
  #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  #store = inject<Store<AppState>>(Store);
  #panel = inject(MyRequestsPanelStore);

  #authUserId = this.#store.selectSignal(selectAuthUserId);
  #permissionState = this.#store.selectSignal(getPermissionState);
  #teams = this.#store.selectSignal(getTeamEntities);
  #shifts = this.#store.selectSignal(getShiftEntities);
  #departments = this.#store.selectSignal(getDepartmentEntities);
  #locations = this.#store.selectSignal(getLocationEntities);
  #employees = this.#store.selectSignal(getEmployeeEntities);
  #absenteeOptions = this.#store.selectSignal(getAbsenteeOptionEntities);
  #employeeTeamDepartments = this.#store.selectSignal(getEmployeeTeamDepartments);

  loading = this.#panel.isLoading;
  loadingMore = this.#panel.isRefreshing;
  hasMore = this.#panel.hasMore;
  isError = this.#panel.isError;
  loadMoreFailed = this.#panel.loadMoreFailed;

  #exchangeViewContext = useExchangeViewContext();

  incomingExchanges = computed(() =>
    filterIncomingExchanges(
      toVisibleExchanges(this.#panel.incomingExchangeRows(), this.#exchangeViewContext()),
      this.#authUserId(),
    ),
  );
  outgoingExchanges = computed(() =>
    filterOutgoingExchanges(
      toVisibleExchanges(this.#panel.outgoingExchangeRows(), this.#exchangeViewContext()),
      this.#authUserId(),
    ),
  );

  openShifts = computed(() =>
    toVisibleOpenShifts(this.#panel.openShiftRows(), {
      teams: this.#teams(),
      shifts: this.#shifts(),
      departments: this.#departments(),
      locations: this.#locations(),
      employees: this.#employees(),
      authenticatedUserId: this.#authUserId(),
    }),
  );

  pendingAbsences = computed(() =>
    toOwnPendingAbsences(this.#panel.ownAbsenceRows(), {
      employees: this.#employees(),
      absenteeOptions: this.#absenteeOptions(),
      employeeTeamDepartments: this.#employeeTeamDepartments(),
      approvableTeamDepartments: this.#employeeTeamDepartments(),
      permissionState: this.#permissionState(),
    }),
  );

  noRequests = computed(
    () =>
      !this.hasMore() &&
      this.incomingExchanges().length === 0 &&
      this.outgoingExchanges().length === 0 &&
      this.openShifts().length === 0 &&
      this.pendingAbsences().length === 0,
  );

  planType = PlanType;
  exchangeRequestContext = ExchangeRequestContext;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconClipboardRegular, mgcIconWarningRegular]);

    effect(() => {
      if (this.#authUserId()) {
        untracked(() => this.#panel.loadNextPage());
      }
    });
  }

  retryLoadMore(): void {
    this.#panel.loadNextPage();
  }

  retryInitialLoad(): void {
    this.#panel.loadNextPage();
    this.#elementRef.nativeElement.focus();
  }
}
